import { useDebounce, useSet } from '@uidotdev/usehooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DocumentActions } from '../../../reflux/document_store';

import Modal from '../../../../realize_overrides/components/modal/modal';
import { ModalActions } from '../../../../realize_overrides/stores/modal_store';

import { convertBytes } from '../../../../utils/convert_bytes';

import { ExportMultipleContent } from './ExportMultipleContent';
import { NotFound } from './NotFound';
import { SearchBar } from './SearchBar';

import './styles.scss';

const Realize = {
  i18n: window.Realize.i18n,
  Modal: Modal,
  ModalHeader: window.ModalHeader,
  ModalContent: window.ModalContent,
  ModalFooter: window.ModalFooter,
};

export function ExportMultipleModal() {
  const messageRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [isAllDocumentsSelected, setIsAllDocumentsSelected] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isMessageRequestVisible, setIsMessageRequestVisible] = useState(false);
  const [messageRequest, setMessageRequest] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [errors, setErrors] = useState(null);
  const [totalUniqueFiles, setTotalUniqueFiles] = useState(0);

  const debouncedSearchField = useDebounce(searchTerm, 300);
  const selectedDocumentsSet = useSet([]);

  const documentsFiltered = useMemo(() => {
    if (!debouncedSearchField || debouncedSearchField.trim().length === 0) {
      return documents;
    }

    const searchTerm = debouncedSearchField.trim().toLowerCase();

    return documents.filter((document) => {
      if (document.fileName.toLowerCase().includes(searchTerm)) {
        return true;
      }

      return document.fields.some(
        (field) =>
          field.label.toLowerCase().includes(searchTerm) ||
          field.name.toLowerCase().includes(searchTerm)
      );
    });
  }, [documents, debouncedSearchField]);

  const modalReady = () => {
    setIsOpen(true);
  };

  const resetStates = () => {
    selectedDocumentsSet.clear();

    setErrors(null);
    setIsOpen(false);
    setDocuments([]);
    setMessageRequest(null);
    setIsAllDocumentsSelected(false);
  };

  const dismissModal = () => {
    window.exportMultipleGridId = null;

    setTimeout(() => {
      resetStates();
    }, 500);

    ModalActions.close({ modalId: 'exportMultipleModal' });
  };

  const showMessagesRequest = (response) => {
    switch (response.status) {
      case 200: {
        setMessageRequest({
          type: 'success',
          message: Realize.i18n.t('exportMultipleModal.success_message'),
        });
        break;
      }

      case 400: {
        if (response.errors[0].code === 'export_multiple_documents_over_limit') {
          const exportationLimit = convertBytes(response.errors[0].content.limit_of_exportation);
          const exportationSize = convertBytes(response.errors[0].content.size_of_exportation);

          setMessageRequest({
            type: 'error',
            message: Realize.i18n
              .t('exportMultipleModal.error.size_exceeded_error')
              .replace('#size_of_exportation', exportationSize)
              .replace('#limit_of_exportation', exportationLimit),
          });
          break;
        }

        setMessageRequest({
          type: 'error',
          message: Realize.i18n.t('exportMultipleModal.error.export_files_error')
        });

        setErrors(response.errors);
        break;
      }

      case 503: {
        setMessageRequest({
          type: 'error',
          message: Realize.i18n.t('exportMultipleModal.error.ecm_connection_error')
        });
        break;
      }

      default: {
        setMessageRequest({
          type: 'error',
          message: Realize.i18n.t('exportMultipleModal.error.unexpected_error'),
        });
      }
    }

    setIsMessageRequestVisible(true);
    setIsDownloading(false);
  };

  const handleExportDocuments = async () => {
    setIsDownloading(true);
    setErrors(null);

    const documentsToExportIds = Array.from(selectedDocumentsSet.values());

    DocumentActions.downloadMultipleFiles(documentsToExportIds, showMessagesRequest);

    setIsMessageRequestVisible(false);
    setMessageRequest(null);
  };

  const buildButtonGroupProps = () => {
    const hasNoSelectedDocuments = selectedDocumentsSet.size === 0;

    const exportButtonProps = {
      name: isDownloading
        ? Realize.i18n.t('exportMultipleModal.exporting')
        : Realize.i18n.t('exportMultipleModal.export'),
      element: 'button',
      style: 'primary',
      role: 'button',
      icon: isDownloading ? 'refresh' : 'file_download',
      onClick: handleExportDocuments,
      className: `export-documents-button ${isDownloading && 'loading'} ${(hasNoSelectedDocuments || isDownloading) && 'disabled'}`,
      disabled: hasNoSelectedDocuments || isDownloading,
    };

    const cancelButtonProps = {
      name: Realize.i18n.t('actions.close'),
      element: 'button',
      style: 'cancel',
      role: 'button',
      icon: 'close',
      onClick: dismissModal,
      className: `button-close-modal-document ${isDownloading ? 'disabled' : ''}`,
      disabled: isDownloading,
    };

    if (documents.length === 0) {
      return [cancelButtonProps];
    }

    return [cancelButtonProps, exportButtonProps];
  };

  const setAllSelected = useCallback(() => {
    if (documentsFiltered.length === 0) return;

    const uuids = documentsFiltered.map((document) => document.fileUuid);
    const isAllSelected = uuids.every((uuid) => selectedDocumentsSet.has(uuid));

    setIsAllDocumentsSelected(isAllSelected);
  }, [selectedDocumentsSet, documentsFiltered]);

  const toggleAllDocumentsSelected = () => {
    if (isAllDocumentsSelected) {
      documentsFiltered.forEach((documentFilterd) => {
        selectedDocumentsSet.delete(documentFilterd.fileUuid);
      });

      setIsAllDocumentsSelected((state) => !state);
      return;
    }

    documentsFiltered.map((document) => {
      return selectedDocumentsSet.add(document.fileUuid);
    });

    setIsAllDocumentsSelected((state) => !state);
  };

  const toggleSelectedDocument = (fileUuid) => {
    if (selectedDocumentsSet.has(fileUuid)) {
      selectedDocumentsSet.delete(fileUuid);
      setIsAllDocumentsSelected(false);
      return;
    }

    selectedDocumentsSet.add(fileUuid);

    setAllSelected();
  };

  const groupDocumentsByFileId = (data) => {
    return data.reduce((acc, document) => {
      const isDocumentReduced = acc.find((accumulator) => {
        return accumulator.fileUuid === document.fileUuid;
      });

      if (!isDocumentReduced) {
        const documentUpdated = {
          fileUuid: document.fileUuid,
          fileName: document.fileName,
          errors: document.errors,
          fields: [
            {
              name: document.fieldName,
              label: document.fieldLabel,
            },
          ],
        };

        return [...acc, documentUpdated];
      }

      const documentsUpdated = acc.map((accumulatorValue) => {
        if (accumulatorValue.fileUuid !== document.fileUuid) {
          return accumulatorValue;
        }

        return {
          ...accumulatorValue,
          fields: [
            ...accumulatorValue.fields,
            {
              name: document.fieldName,
              label: document.fieldLabel,
            },
          ],
        };
      });

      return documentsUpdated;
    }, []);
  };

  const getDocumentsInGrid = ({ gridIdentifier, fieldName }) => {
    const gridAPI = Form.grids(gridIdentifier);
    const dataRows = gridAPI.dataRows();

    const isGridVisible = gridAPI.visible();

    if (!isGridVisible) {
      return [];
    }

    const documentsInGrid = dataRows.reduce((acc, dataRow) => {
      const isGridColumnVisible = gridAPI.columns(fieldName).visible();

      if (!isGridColumnVisible) {
        return acc;
      }

      const documentRowValue = dataRow[fieldName];

      if (documentRowValue) {
        const [fileName, fileUuid] = documentRowValue.split(':');
        const fieldLabel = gridAPI.fields(fieldName).label() || Realize.i18n.t('exportMultipleModal.field_without_name');

        const documentData = {
          fileUuid,
          fileName,
          fieldName,
          fieldLabel,
          error: null,
        };

        return [...acc, documentData];
      }

      return acc;
    }, []);

    return documentsInGrid;
  };

  const getDocumentsFromStore = useCallback(() => {
    const documentStore = Object
      .entries(Lecom.stores.DocumentStore.store)
      .map(([key, value]) => ({ key, value }));

    const data = documentStore.reduce((acc, item) => {
      const document = item.value._document;
      const fileUuid = document._fileUUID;

      const gridIdentifier = item.value._gridData._gridIdentifier;

      if (!fileUuid && !gridIdentifier) {
        return acc;
      }

      const fieldName = item.key;

      if (gridIdentifier) {
        const documentsInGrid = getDocumentsInGrid({ gridIdentifier, fieldName });

        return [...acc, ...documentsInGrid];
      }

      const isFieldVisible = Form.fields(fieldName).visible();

      if (!isFieldVisible) {
        return acc;
      }

      const fileName = document._fileName;
      const fieldLabel = Form.fields(fieldName).label() || Realize.i18n.t('exportMultipleModal.field_without_name');

      const documentData = {
        fileUuid,
        fileName,
        fieldName,
        fieldLabel,
        error: null,
      };

      return [...acc, documentData];
    }, []);

    const groupedDocuments = groupDocumentsByFileId(data);

    setDocuments(groupedDocuments);
    setTotalUniqueFiles(groupedDocuments.length);
  }, []);

  const getDocumentsFromGrid = useCallback(() => {
    const gridId = window.exportMultipleGridId;

    const documentStore = Object
      .entries(Lecom.stores.DocumentStore.store)
      .map(([key, value]) => ({ key, value }));

    const data = documentStore.reduce((acc, document) => {
      const gridIdentifier = document.value._gridData._gridIdentifier;
      const fieldName = document.key;

      if (gridIdentifier === gridId) {
        const documentsInGrid = getDocumentsInGrid({ gridIdentifier, fieldName });

        return [...acc, ...documentsInGrid];
      }

      return acc;
    }, []);

    const groupedDocuments = groupDocumentsByFileId(data);

    setDocuments(groupedDocuments);
    setTotalUniqueFiles(groupedDocuments.length);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    if (window.exportMultipleGridId) {
      getDocumentsFromGrid();
      return;
    }

    getDocumentsFromStore();
  }, [isOpen, getDocumentsFromStore, getDocumentsFromGrid]);

  useEffect(() => {
    setAllSelected();
  }, [setAllSelected]);

  useEffect(() => {
    if (isMessageRequestVisible) {
      messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      messageRef.current.focus();
    }
  }, [isMessageRequestVisible, messageRef]);

  const documentsUpdatedWithErrors = useMemo(() => {
    return documentsFiltered.reduce((acc, document) => {
      const error = errors?.find((error) => error.identification === document.fileUuid);

      if (error) {
        const documentWithError = { ...document, error };

        return [documentWithError, ...acc];
      }

      return [...acc, document];
    }, []);
  }, [documentsFiltered, errors]);

  return (
    <div className={`container-export-multiple-modal ${isOpen ? 'open' : ''}`}>
      <Realize.Modal
        id="exportMultipleModal"
        key="exportMultipleModal"
        dismissible={false}
        ready={modalReady}
      >
        <Realize.ModalHeader>
          <span className="h5-custom">
            {Realize.i18n.t('exportMultipleModal.modalTitle')}
          </span>
        </Realize.ModalHeader>

        <Realize.ModalContent>
          {isMessageRequestVisible && messageRequest && (
            <div
              ref={messageRef}
              className={`flash-message ${messageRequest.type}`}
            >
              {messageRequest.message}
            </div>
          )}

          {documents.length > 0 ? (
            <>
              <SearchBar onSearch={setSearchTerm} searchValue={searchTerm} />

              <ExportMultipleContent
                data={documentsUpdatedWithErrors}
                errors={errors}
                selectedDocuments={selectedDocumentsSet}
                isAllDocumentsSelected={isAllDocumentsSelected}
                onToggleSelectedDocument={toggleSelectedDocument}
                onToggleAllDocumentsSelected={toggleAllDocumentsSelected}
              />
            </>
          ) : (
            <NotFound />
          )}
        </Realize.ModalContent>

        <Realize.ModalFooter className="flex-space-between">
          {documents.length > 0 ? (
            <p className="total-select-amount">
              {Realize.i18n.t('exportMultipleModal.was_selected')}{' '}
              <span>{selectedDocumentsSet.size}</span>{' '}
              {Realize.i18n.t('exportMultipleModal.of')}{' '}
              <span>{totalUniqueFiles}</span>{' '}
              {Realize.i18n.t('exportMultipleModal.total_files')}.
            </p>
          ) : (
            <div></div>
          )}

          <ButtonGroup buttons={buildButtonGroupProps()} />
        </Realize.ModalFooter>
      </Realize.Modal>
    </div>
  );
}
